import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-app-hero.css?inline';

/**
 * `<pk-app-hero>` — the dark ink band beneath the site header on dashboard/admin pages.
 * Carries an eyebrow label, a page H1, a breadcrumb, and optional right-side actions.
 *
 * Slots:
 *   - `eyebrow`    — small-caps label above the title (falls back to `eyebrow` attribute)
 *   - default      — H1 title content (falls back to `title` attribute)
 *   - `meta`       — small chips/pills shown inline next to the title (e.g. Site ID + role pills)
 *   - `breadcrumb` — breadcrumb row, rendered beneath the title
 *   - `actions`    — right-side actions (buttons, etc.)
 *
 * Attributes:
 *   - `eyebrow`   — plain-text eyebrow when no slot is provided
 *   - `title`     — plain-text H1 when no slot is provided
 */
export class PkAppHero extends HTMLElement {
    static readonly tagName = 'pk-app-hero';

    static get observedAttributes(): string[] {
        return ['eyebrow', 'title'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private template(): TemplateResult {
        const eyebrow = this.getAttribute('eyebrow');
        const title   = this.getAttribute('title');

        return html`
            <style>${styles}</style>
            <div class="hero-inner">
                <div class="hero-main">
                    <div class="eyebrow-wrap">
                        <slot name="eyebrow">
                            ${eyebrow ? html`<span class="eyebrow-default">${eyebrow}</span>` : ''}
                        </slot>
                    </div>
                    <div class="title-row">
                        <div class="title-wrap">
                            <slot>
                                ${title ? html`<h1 class="title-default">${title}</h1>` : ''}
                            </slot>
                        </div>
                        <div class="meta-wrap">
                            <slot name="meta"></slot>
                        </div>
                    </div>
                    <div class="breadcrumb-wrap">
                        <slot name="breadcrumb"></slot>
                    </div>
                </div>
                <div class="hero-actions">
                    <slot name="actions"></slot>
                </div>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkAppHero.tagName)) {
    customElements.define(PkAppHero.tagName, PkAppHero);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-app-hero': PkAppHero;
    }
}
